// The search title and description of a public college page (#3251).
//
// Founder, 2026-10-06 ("ok proceed" on the proposal in
// marketing/seo/college-titles-proposal-2026-10-06.md): the title answers the
// search people make most, "<school> acceptance rate", with the number. The
// old title ("<Name> admissions: acceptance rate, deadlines, cost | Stratia
// Admissions", about 84 characters) was cut by Google before any number.
//
// Rules a future editor must keep:
//  - A title is at most TITLE_MAX characters: " | Stratia" goes first, then
//    the full name gives way to the short name. Never cut mid-name.
//  - A number shows only when the KB holds it: the rate appears only where
//    the KB's overall_acceptance_rate (kbFacts.json, a read-only snapshot)
//    rounds to the page's own count-derived rate. Otherwise the title names
//    no number at all (live 2026-10-06: four pages, Northeastern among them,
//    where page and KB disagree).
//  - An override (seoOverrides.json) is for a page whose search asks for
//    something else (a deadline, tuition) and lists every figure it shows
//    with the KB path that holds it; collegeMeta.test.js checks both.
import kbFacts from '../data/colleges/kbFacts.json';
import overrides from '../data/colleges/seoOverrides.json';
import { acceptanceRate } from '../data/colleges';

export const TITLE_MAX = 60;
export const DESCRIPTION_MAX = 155;
const SUFFIX = ' | Stratia';

/** The rate the title may show: the page's, when the KB agrees; else null. */
export function verifiedRate(c) {
    const kb = kbFacts.schools?.[c.slug]?.overall_acceptance_rate;
    if (typeof kb !== 'number' || !c.admission?.applied) return null;
    const page = acceptanceRate(c);
    return Math.round(kb * 10) / 10 === page ? page : null;
}

export function collegeOverride(slug) {
    return slug && !slug.startsWith('_') && Object.hasOwn(overrides, slug) ? overrides[slug] : null;
}

function firstThatFits(parts) {
    for (const t of parts) if (t.length <= TITLE_MAX) return t;
    return parts[parts.length - 1].slice(0, TITLE_MAX).trim();
}

export function collegeTitle(c) {
    const o = collegeOverride(c.slug);
    if (o?.title) return o.title;
    const names = [...new Set([c.name, c.shortName].filter(Boolean))];
    const rate = verifiedRate(c);
    const bodies = rate === null
        ? names.flatMap((n) => [`${n} admissions: deadlines, testing, cost`, `${n} admissions and deadlines`])
        : names.map((n) => `${n} acceptance rate: ${rate}% (${c.admission.entering})`);
    return firstThatFits(bodies.flatMap((b) => [b + SUFFIX, b]));
}

export function collegeDescription(c) {
    const o = collegeOverride(c.slug);
    if (o?.description) return o.description;
    const rate = acceptanceRate(c);
    return `${c.name} admitted ${rate}% of first-year applicants for ${c.admission.entering}${c.admission.cdsCycle ? ` (${c.shortName}'s Common Data Set ${c.admission.cdsCycle})` : ''}. Deadlines for ${c.deadlines.entering}, test policy, and cost after aid, each from its official source.`;
}
